import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";

export default function InquiryDialog({ open, onClose, product }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && product) {
            setForm((f) => ({ ...f, message: `I'm interested in the ${product.name} (${product.brand}). Please share pricing, availability and delivery timelines.` }));
        }
    }, [open, product]);

    if (!open) return null;

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please complete all required fields.");
            return;
        }
        setLoading(true);
        try {
            await submitInquiry({
                inquiry_type: "product",
                subject: `Product Inquiry — ${product?.name}`,
                product_id: product?.id,
                product_name: product?.name,
                ...form,
            });
            toast.success("Inquiry sent. Our sales team will call you shortly.");
            onClose();
        } catch {
            toast.error("Failed to send inquiry. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="inquiry-dialog">
            <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={onClose} />
            <form
                onSubmit={onSubmit}
                className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
            >
                <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-brand">Product Inquiry</div>
                        <h3 className="mt-1 font-display text-xl font-bold text-navy">{product?.name}</h3>
                        <div className="text-sm text-slate-500 mt-0.5">{product?.brand} · {product?.category}</div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="inquiry-dialog-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 grid sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                    <Field label="Full name" required>
                        <input className={ip} value={form.name} onChange={upd("name")} data-testid="inquiry-input-name" placeholder="Your name" />
                    </Field>
                    <Field label="Phone" required>
                        <input className={ip} value={form.phone} onChange={upd("phone")} data-testid="inquiry-input-phone" placeholder="+91 …" />
                    </Field>
                    <Field label="Email" required>
                        <input type="email" className={ip} value={form.email} onChange={upd("email")} data-testid="inquiry-input-email" placeholder="you@company.com" />
                    </Field>
                    <Field label="Company">
                        <input className={ip} value={form.company} onChange={upd("company")} data-testid="inquiry-input-company" placeholder="Company" />
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label="Requirement" required>
                            <textarea rows={4} className={`${ip} resize-none`} value={form.message} onChange={upd("message")} data-testid="inquiry-input-message" />
                        </Field>
                    </div>
                </div>

                <div className="p-4 sm:p-5 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
                    <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:bg-slate-100" data-testid="inquiry-cancel-btn">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="inquiry-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold transition-colors">
                        <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send Inquiry"}
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
}

const ip = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-colors";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                {label} {required && <span className="text-brand">*</span>}
            </span>
            {children}
        </label>
    );
}
